import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import * as turf from '@turf/turf';
import { geoXplore } from 'src/environments/environment';
import { MixDesignService } from 'src/app/shared/services/mix-design.service';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MixDesignDto, MixDesignLocationResultDto } from 'src/app/shared/models/mix-design-location.model';
import { MixDesignListDialogComponent } from './components/mix-design-list-dialog/mix-design-list-dialog.component';

@Component({
    selector: 'app-geo-map-precast',
    templateUrl: './geo-map-precast.component.html',
    styleUrls: ['./geo-map-precast.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeoMapPrecastComponent implements OnInit, OnDestroy {
  public map: mapboxgl.Map;
  public style = 'mapbox://styles/mapbox/streets-v12';
  public sliderValue = 50;
  public latitude: number | null = null;
  public longitude: number | null = null;
  public materialTypes: string[] = [];
  public selectedMaterialTypes: string[] = [];

  private mixDesignResult: MixDesignLocationResultDto | null = null;
  private mixDesignSubscription?: Subscription;

  geocoder: MapboxGeocoder;

  private selectionMarker?: mapboxgl.Marker;
  private mixDesignMarkers: mapboxgl.Marker[] = [];
  private readonly circleSourceId = 'selected-radius-source';
  private readonly circleFillLayerId = 'selected-radius-fill';
  private readonly circleOutlineLayerId = 'selected-radius-outline';

  constructor(
    private mixDesignService: MixDesignService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.initMap();
  }

  ngOnDestroy() {
    this.mixDesignSubscription?.unsubscribe();
    this.map?.remove();
  }

  initMap() {
    this.map = new mapboxgl.Map({
      accessToken: geoXplore.accessToken,
      container: 'precast-map',
      style: this.style,
      center: [-98.5795, 39.8283],
      zoom: 2,
    });

    this.geocoder = new MapboxGeocoder({
      accessToken: geoXplore.accessToken,
      types: 'district, place, locality, neighborhood, address',
      placeholder: 'Enter county, city, or location...',
      mapboxgl,
    });

    const geocoderContainer = document.getElementById('precast-geocoder');

    if (geocoderContainer) {
      geocoderContainer.innerHTML = '';
      this.geocoder.addTo('#precast-geocoder');
    }

    this.geocoder.on('result', (e) => {
      const [long, lat] = e.result.center;
      this.longitude = long;
      this.latitude = lat;
      this.resetMixDesignResults();
      this.addOrUpdateMarker();
      this.drawRadiusCircle();
      this.zoomToLocation();
    });

    this.geocoder.on('clear', () => {
      this.longitude = null;
      this.latitude = null;
      this.resetMixDesignResults();
      this.clearMarkerAndCircle();
    });

    setTimeout(() => {
      this.map.flyTo({
        center: [-98.5795, 39.8283],
        zoom: 3,
        speed: 0.5,
        curve: 0.8,
      });
    }, 1600);
    this.map.addControl(new mapboxgl.NavigationControl());
  }

  onArrowClick(): void {
    if (!this.hasValidLatLng || this.longitude === null || this.latitude === null) return;

    this.mixDesignSubscription?.unsubscribe();
    this.mixDesignSubscription = this.mixDesignService.getMixDesignsByLocation(this.latitude, this.longitude, this.sliderValue).subscribe({
      next: (result) => {
        this.mixDesignResult = result;
        this.materialTypes = Object.keys(result.coordinatesByMaterialType ?? {
        }).filter((materialType) => materialType.trim() !== '').sort();
        this.selectedMaterialTypes = [];
        this.renderMixDesignMarkers();
      },
    });
  }

  onMaterialTypeChange(materialTypes: string[]): void {
    const selectedTypes = materialTypes;
    this.selectedMaterialTypes = selectedTypes;
    this.renderMixDesignMarkers();
  }

  private renderMixDesignMarkers(): void {
    this.clearMixDesignMarkers();
    if (!this.mixDesignResult) return;

    const { coordinatesByMaterialType } = this.mixDesignResult;
    const allowedCoordinates = new Set(this.selectedMaterialTypes
      .flatMap((materialType) => coordinatesByMaterialType?.[materialType as string] ?? [])
      .map(({ latitude, longitude }) => this.toCoordinateKey(latitude, longitude)));

    Object.entries(this.mixDesignResult.coordinates ?? {
    }).forEach(([key, mixDesigns]) => {
      const [lat, long] = key.split(',').map(Number);
      if (!Number.isFinite(lat) || !Number.isFinite(long)) return;
      if (allowedCoordinates && !allowedCoordinates.has(this.toCoordinateKey(lat, long))) return;

      const marker = new mapboxgl.Marker({
        color: '#FF5733',
      })
        .setLngLat([long, lat])
        .addTo(this.map);
      marker.getElement().addEventListener('click', () => {
        this.openMixDesignDialog(mixDesigns);
      });
      this.mixDesignMarkers.push(marker);
    });
  }

  private toCoordinateKey(latitude: number, longitude: number): string {
    return `${Number(latitude)},${Number(longitude)}`;
  }

  private resetMixDesignResults(): void {
    this.mixDesignSubscription?.unsubscribe();
    this.mixDesignResult = null;
    this.materialTypes = [];
    this.selectedMaterialTypes = [];
    this.clearMixDesignMarkers();
  }

  private openMixDesignDialog(mixDesigns: MixDesignDto[]): void {
    this.dialog.open(MixDesignListDialogComponent, {
      data: mixDesigns,
      width: '480px',
      maxWidth: '95vw',
      maxHeight: '80vh',
    });
  }

  private clearMixDesignMarkers(): void {
    this.mixDesignMarkers.forEach((marker) => marker.remove());
    this.mixDesignMarkers = [];
  }

  onSliderChange(event: Event): void {
    const { value } = event.target as HTMLInputElement;
    this.sliderValue = Number(value);
    this.drawRadiusCircle();
  }

  get hasValidLatLng(): boolean {
    const { latitude, longitude } = this;
    if (latitude !== null && longitude !== null) {
      return Number.isFinite(latitude)
            && Number.isFinite(longitude)
            && latitude >= -90 && latitude <= 90
            && longitude >= -180 && longitude <= 180;
    }
    return false;
  }

  private zoomToLocation() {
    setTimeout(() => {
      if (this.longitude === null || this.latitude === null) return;
      this.map.flyTo({
        center: [this.longitude, this.latitude],
        zoom: 6.5,
        speed: 0.5,
        curve: 0.8,
      });
    }, 200);
  }

  private addOrUpdateMarker() {
    if (this.longitude !== null && this.latitude !== null) {
      if (!this.selectionMarker) {
        this.selectionMarker = new mapboxgl.Marker({
          color: '#1E90FF',
        })
          .setLngLat([this.longitude, this.latitude])
          .addTo(this.map);
      } else {
        this.selectionMarker.setLngLat([this.longitude, this.latitude]);
      }
    }
  }

  private drawRadiusCircle() {
    if (this.longitude !== null && this.latitude !== null) {
      const circle = turf.circle([this.longitude, this.latitude], this.sliderValue, {
        units: 'miles',
      });

      const existing = this.map.getSource(this.circleSourceId) as mapboxgl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(circle);
        return;
      }

      this.map.addSource(this.circleSourceId, {
        type: 'geojson',
        data: circle,
      });

      this.map.addLayer({
        id: this.circleFillLayerId,
        type: 'fill',
        source: this.circleSourceId,
        paint: {
          'fill-color': '#1E90FF',
          'fill-opacity': 0.15,
        },
      });

      this.map.addLayer({
        id: this.circleOutlineLayerId,
        type: 'line',
        source: this.circleSourceId,
        paint: {
          'line-color': '#1E90FF',
          'line-width': 2,
        },
      });
    }
  }

  private clearMarkerAndCircle(): void {
    if (this.selectionMarker) {
      this.selectionMarker.remove();
      this.selectionMarker = undefined;
    }

    if (this.map.getLayer(this.circleFillLayerId)) {
      this.map.removeLayer(this.circleFillLayerId);
    }
    if (this.map.getLayer(this.circleOutlineLayerId)) {
      this.map.removeLayer(this.circleOutlineLayerId);
    }

    if (this.map.getSource(this.circleSourceId)) {
      this.map.removeSource(this.circleSourceId);
    }
  }
}
